import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate, useRouteContext } from "@tanstack/react-router";
import type { JSX } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { graphql } from "@repo/graphql-types";
import ArrowRight from "@repo/icons/default/ArrowRight";
import { CpsTextArea } from "corpus";

import { FlowHeader } from "@/components/ui/flow-header/flow-header";
import {
  FormControl,
  FormField,
  FormHandlerSubmit,
  FormItem,
  FormRoot,
  FormSubmitButton,
} from "@/components/ui/form/form";
import { Page, PageTitle } from "@/components/ui/layout/page/page";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import {
  CardSelectItem,
  CardSelectItemContainer,
  CardSelectRoot,
} from "@/components/ui/select-input/card-select";
import { useGraphQLMutationWithErrorHandler } from "@/hooks/use-graphql";

const SKIP_REASONS = [
  { value: "ex-membro", label: "Sou ex-membro" },
  { value: "ja-entendeu-o-sistema", label: "Já entendi como funciona o sistema" },
  { value: "sem-tempo", label: "Não tenho tempo" },
  { value: "outro", label: "Outro" },
] as const;

const OTHER_REASON = "outro";
const REASON_DETAIL_MAX_LENGTH = 500;
const REASON_DETAIL_LABEL = "Conte para a gente o motivo (opcional)";

const skipReasonSchema = z.object({
  reason: z.string().min(1, "Escolha um motivo"),
  reasonDetail: z
    .string()
    .max(REASON_DETAIL_MAX_LENGTH, `Use até ${REASON_DETAIL_MAX_LENGTH} caracteres`),
});

type SkipReasonFormFields = z.infer<typeof skipReasonSchema>;

const DismissOnboardingMutation = graphql(`
 mutation InsertOnboardingVisitDismissal($livanceCoreId: Int!, $reason: String!) {
  insert_onboarding_visit_dismissal_one(
    object: { livanceCoreId: $livanceCoreId, reason: $reason }
    on_conflict: {
      constraint: onboarding_visit_dismissal_livance_core_id_key
      update_columns: [reason, dismissedAt]
    }
  ) {
    id
  }
}
`);

const SkipReasonPage = (): JSX.Element => {
  const navigate = useNavigate();
  const { user } = useRouteContext({ from: "__root__" });
  const dismissMutation = useGraphQLMutationWithErrorHandler(DismissOnboardingMutation);
  const form = useForm<SkipReasonFormFields>({
    resolver: zodResolver(skipReasonSchema),
    defaultValues: { reason: "", reasonDetail: "" },
  });

  const selectedReason = form.watch("reason");
  const isOtherReason = selectedReason === OTHER_REASON;

  const handleSubmit = ({ reason, reasonDetail }: SkipReasonFormFields): void => {
    const detail = reason === OTHER_REASON ? reasonDetail.trim() || null : null;

    dismissMutation.mutate(
      {
        livanceCoreId: user.codUsuario,
        reason: detail ?? reason,
      },
      {
        onSuccess: () => {
          void navigate({ to: "/onboarding/welcome-meeting/dismissed" });
        },
      },
    );
  };

  return (
    <RoundedLayout>
      <FlowHeader>
        <FlowHeader.Actions>
          <FlowHeader.BackButton />
          <FlowHeader.HelpButton />
        </FlowHeader.Actions>
        <FlowHeader.ProgressBar progress={1 / 2} />
      </FlowHeader>

      <Page
        className="flex flex-col justify-between pb-0 px-6"
        showCustomerIoContainer={false}
      >
        <FormRoot {...form}>
          <FormHandlerSubmit
            handleSubmit={handleSubmit}
            className="mx-0 max-w-none flex-1 justify-between gap-0"
          >
            <div className="flex flex-col gap-4">
              <div>
                <PageTitle>
                  Por qual motivo você deseja pular a reunião de boas vindas?
                </PageTitle>
              </div>

              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div>
                        <CardSelectRoot
                          name="skip-reason"
                          mode="single"
                          defaultValue={field.value || undefined}
                          onChange={field.onChange}
                        >
                          <CardSelectItemContainer>
                            {SKIP_REASONS.map((reason) => (
                              <CardSelectItem key={reason.value} value={reason.value}>
                                {reason.label}
                              </CardSelectItem>
                            ))}
                          </CardSelectItemContainer>
                        </CardSelectRoot>
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />

              {isOtherReason ? (
                <FormField
                  control={form.control}
                  name="reasonDetail"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <CpsTextArea
                          title={REASON_DETAIL_LABEL}
                          aria-label={REASON_DETAIL_LABEL}
                          placeholder="Digite aqui"
                          maxLength={REASON_DETAIL_MAX_LENGTH}
                          rows={4}
                          className="resize-none pr-4"
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              ) : null}
            </div>

            <div className="mt-auto">
              <div className="sticky bottom-0 flex flex-col gap-3 pt-4 pb-6 bg-white">
                <FormSubmitButton
                  color="secondary"
                  disabled={!selectedReason || dismissMutation.isPending}
                  loading={dismissMutation.isPending}
                >
                  <div className="flex flex-row items-center justify-center gap-2">
                    Continuar
                    <ArrowRight size={24} aria-hidden="true" />
                  </div>
                </FormSubmitButton>
              </div>
            </div>
          </FormHandlerSubmit>
        </FormRoot>
      </Page>
    </RoundedLayout>
  );
};

export const Route = createFileRoute("/onboarding/welcome-meeting/skip-reason")({
  component: SkipReasonPage,
});
